import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  MenuItem,
  Select,
  OutlinedInput,
  InputLabel,
  FormControl,
  Typography,
  InputAdornment,
  Popover,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useSelector } from "react-redux";
import moment from "moment";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import "./style.css";
import { HeaderIconsSection } from "../../components/headerSection/headerIconsSection";

export const CustomToolbar = ({
  accountManagers,
  branches = [],
  filterParams,
  setFilterParams,
  setShowUnconfirmedOnly,
}) => {
  const dateTypes = {
    today: "TODAY",
    thisWeek: "THIS_WEEK",
    thisMonth: "THIS_MONTH",
  };

  const [selectedDate, setSelectedDate] = useState({
    type: dateTypes.thisWeek,
    count: 0,
  });

  const { tableLoading } = useSelector((s) => s.loading);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [dateRange, setDateRange] = useState({
    jobDateFrom: filterParams.jobDateFrom,
    jobDateTo: filterParams.jobDateTo,
  });

  const [localFilters, setLocalFilters] = useState({
    accountManagers: filterParams.accountManagers || [],
    branches: filterParams.branches || [],
    jobDateFrom: filterParams.jobDateFrom,
    jobDateTo: filterParams.jobDateTo,
  });

  const from = localFilters.jobDateFrom
    ? moment(localFilters.jobDateFrom).format("D MMM YYYY")
    : "";
  const to = localFilters.jobDateTo
    ? moment(localFilters.jobDateTo).format("D MMM YYYY")
    : "";

  const setFilterDateRange = (start, end) => {
    setLocalFilters({
      ...localFilters,
      jobDateFrom: moment(start).format("YYYY-MM-DD"),
      jobDateTo: moment(end).format("YYYY-MM-DD"),
    });
  };

  const updateDateRange = (type, count) => {
    let start, end;
    if (type === dateTypes.today) {
      start = moment().add(count, "days");
      end = start;
    } else if (type === dateTypes.thisWeek) {
      start = moment().startOf("week").add(1, "day").add(count, "weeks");
      end = moment().endOf("week").add(1, "day").add(count, "weeks");
    } else if (type === dateTypes.thisMonth) {
      start = moment().startOf("month").add(count, "months");
      end = moment().endOf("month").add(count, "months");
    }
    setSelectedDate({ type, count });
    setFilterDateRange(start, end);
  };

  const handlePrevRange = () => {
    const { type, count } = selectedDate;
    updateDateRange(type, count - 1);
  };

  const handleNextRange = () => {
    const { type, count } = selectedDate;
    updateDateRange(type, count + 1);
  };

  const handleToday = () => {
    const today = moment();
    setSelectedDate({ type: dateTypes.today, count: 0 });
    setFilterDateRange(today, today);
    handleClose();
  };

  const handleThisWeek = () => {
    const start = moment().startOf("week").add(1, "day");
    const end = moment().endOf("week").add(1, "day");
    setSelectedDate({ type: dateTypes.thisWeek, count: 0 });
    setFilterDateRange(start, end);
    handleClose();
  };

  const handleThisMonth = () => {
    const start = moment().startOf("month");
    const end = moment().endOf("month");
    setSelectedDate({ type: dateTypes.thisMonth, count: 0 });
    setFilterDateRange(start, end);
    handleClose();
  };

  return (
    <Container
      maxWidth="false"
      style={{ backgroundColor: "#F8F9FD", padding: "0px 8px" }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 1 }}
      >
        <HeaderIconsSection />
        <Box sx={{ alignItems: "center", display: "flex" }}>
          <Box spacing={1} sx={{ alignItems: "center", display: "flex" }}>
            <IconButton onClick={handlePrevRange} disabled={tableLoading}>
              <ArrowBackIosIcon />
            </IconButton>

            <FormControl sx={{ m: 1, width: 280 }}>
              <TextField
                label="Time Range"
                onClick={handleClick}
                value={from && to ? `${from} - ${to}` : ""}
                placeholder="Select date range"
                disabled={tableLoading}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled>
                        <CalendarMonthOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#007BFF" }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  borderColor: "#007BFF",
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 10,
                    border: "none",
                    backgroundColor: "white",
                  },
                }}
                focused
              />
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    Select Job Date Range
                  </Typography>
                  <TextField
                    label="Job Date From"
                    type="date"
                    disabled={tableLoading}
                    value={dateRange.jobDateFrom}
                    onChange={(e) => {
                      setDateRange({
                        ...dateRange,
                        jobDateFrom: e.target.value,
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Job Date To"
                    type="date"
                    disabled={tableLoading}
                    value={dateRange.jobDateTo}
                    onChange={(e) => {
                      setDateRange({ ...dateRange, jobDateTo: e.target.value });
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    disabled={tableLoading}
                    variant={"outlined"}
                    onClick={() => {
                      const updatedFilters = { ...localFilters, ...dateRange };
                      setLocalFilters(updatedFilters);
                      const mappedFilters = {
                        ...updatedFilters,
                        branches: updatedFilters.branches.map((b) =>
                          typeof b === "string" && b === "NY Market"
                            ? "global"
                            : b
                        ),
                      };
                      setFilterParams(mappedFilters);
                      handleClose();
                    }}
                  >
                    Check
                  </Button>
                </Box>
              </Popover>
            </FormControl>

            <IconButton onClick={handleNextRange} disabled={tableLoading}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
          <Box spacing={1} sx={{ alignItems: "center", display: "flex" }}>
            <Button
              variant={"contained"}
              size="small"
              onClick={handleToday}
              sx={{
                mx: 0.5,
                py: 0.7,
                background:
                  selectedDate.type === dateTypes.today ? "#007BFF" : "#EFF1F9",
                color:
                  selectedDate.type === dateTypes.today ? "#fff" : "#2B3033",
                boxShadow: "none",
              }}
            >
              Today
            </Button>
            <Button
              variant={"contained"}
              size="small"
              onClick={handleThisWeek}
              sx={{
                mx: 0.5,
                py: 0.7,
                background:
                  selectedDate.type === dateTypes.thisWeek
                    ? "#007BFF"
                    : "#EFF1F9",
                color:
                  selectedDate.type === dateTypes.thisWeek ? "#fff" : "#2B3033",
                boxShadow: "none",
              }}
            >
              This Week
            </Button>
            <Button
              variant={"contained"}
              size="small"
              onClick={handleThisMonth}
              sx={{
                mx: 0.5,
                py: 0.7,
                background:
                  selectedDate.type === dateTypes.thisMonth
                    ? "#007BFF"
                    : "#EFF1F9",
                color:
                  selectedDate.type === dateTypes.thisMonth
                    ? "#fff"
                    : "#2B3033",
                boxShadow: "none",
              }}
            >
              This Month
            </Button>
          </Box>
          <FormControl sx={{ m: 1, width: 400 }}>
            <InputLabel id="demo-multiple-account-manager-label">
              Account Manager
            </InputLabel>
            <Select
              labelId="demo-multiple-account-manager-label"
              id="demo-multiple-account-manager"
              multiple
              disabled={tableLoading}
              value={localFilters.accountManagers}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  accountManagers: e.target.value,
                })
              }
              input={
                <OutlinedInput
                  id="select-multiple-account-manager"
                  label="Account Manager"
                  sx={{
                    borderRadius: 10,
                    backgroundColor: "white",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "12px 14px",
                      minHeight: "32px !important",
                    },
                  }}
                />
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => {
                    const accountManager = accountManagers.find(
                      (p) => p.id === id
                    );
                    return (
                      <Chip
                        key={id}
                        label={accountManager?.name ?? "Unknown"}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDelete={(e) => {
                          e.stopPropagation();
                          const updated = localFilters.accountManagers.filter(
                            (managerId) => managerId !== id
                          );
                          setLocalFilters({
                            ...localFilters,
                            accountManagers: updated,
                          });
                        }}
                        deleteIcon={<CancelIcon />}
                        sx={{ borderRadius: 3 }}
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 350 } },
              }}
            >
              {accountManagers.map((accountManager) => (
                <MenuItem key={accountManager.id} value={accountManager.id}>
                  {accountManager.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ m: 1, width: 400 }}>
            <InputLabel id="demo-multiple-branches-label">Branches</InputLabel>
            <Select
              labelId="demo-multiple-branches-label"
              id="demo-multiple-branches"
              multiple
              disabled={tableLoading}
              value={localFilters.branches}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, branches: e.target.value })
              }
              input={
                <OutlinedInput
                  id="select-multiple-branches"
                  label="Branches"
                  sx={{
                    borderRadius: 10,
                    backgroundColor: "white",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "12px 14px",
                      minHeight: "32px !important",
                    },
                  }}
                />
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((branch) => (
                    <Chip
                      key={typeof branch === "string" ? branch : branch.id}
                      label={typeof branch === "string" ? branch : branch.name}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDelete={(e) => {
                        e.stopPropagation();
                        const updated = localFilters.branches.filter(
                          (b) => b !== branch
                        );
                        setLocalFilters({ ...localFilters, branches: updated });
                      }}
                      deleteIcon={<CancelIcon />}
                      sx={{ borderRadius: 3 }}
                    />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 350 } },
              }}
            >
              {branches.map((branch) =>
                typeof branch === "string" ? (
                  <MenuItem key={branch} value={branch}>
                    {branch}
                  </MenuItem>
                ) : (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 2, height: 40 }}
            onClick={() => {
              const mappedFilters = {
                ...localFilters,
                branches: localFilters.branches.map((b) =>
                  typeof b === "string" && b === "NY Market" ? "global" : b
                ),
              };
              setFilterParams(mappedFilters);
              // Reset unconfirmed filter to show all jobs
              if (setShowUnconfirmedOnly) {
                setShowUnconfirmedOnly(false);
              }
            }}
            disabled={tableLoading}
          >
            Filter
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
